import React, { useEffect, useState } from "react";
import styled from "styled-components";
import faqs from "../../data/landingFaq.json";

const featured = faqs.filter((faq) => faq.featured);
const more = faqs.filter((faq) => !faq.featured);
const links = {
  "개인정보 처리방침": "/privacypolicy",
  "이용약관": "/termsofservice",
  "lawlow.korea@gmail.com": "mailto:lawlow.korea@gmail.com",
};

function linkedAnswer(answer) {
  return answer.split(/(개인정보 처리방침|이용약관|lawlow\.korea@gmail\.com)/g)
    .map((part, index) => links[part] ? <a key={index} href={links[part]}>{part}</a> : part);
}

const WhyDoesItMatterSection = () => {
  const [showMore, setShowMore] = useState(false);
  const [opened, setOpened] = useState({});

  useEffect(() => {
    let frame;
    const openFromHash = () => {
      const id = window.location.hash.slice(1);
      const faq = faqs.find((item) => item.id === id);
      if (!faq) return;
      if (!faq.featured) setShowMore(true);
      setOpened((current) => ({ ...current, [id]: true }));
      frame = window.requestAnimationFrame(() => {
        const button = document.getElementById(`${id}-question`);
        button?.focus({ preventScroll: true });
        button?.scrollIntoView({ block: "center", behavior: "instant" });
      });
    };
    openFromHash();
    window.addEventListener("hashchange", openFromHash);
    return () => {
      window.cancelAnimationFrame(frame);
      window.removeEventListener("hashchange", openFromHash);
    };
  }, []);

  const begin = () => {
    // Reuse the current composer without submitting or changing its contents.
    const target = [...document.querySelectorAll(
      '[data-guest-experience] textarea:not(:disabled), [data-guest-experience] input:not(:disabled), [data-guest-experience] button[aria-label="로그인하고 대화 계속하기"]'
    )].find((element) => element.getClientRects().length);
    if (target) {
      target.focus({ preventScroll: true });
      target.scrollIntoView({ block: "center", behavior: "instant" });
    }
  };

  const renderFaq = (faq) => (
    <Item data-behavior={faq.id} key={faq.id} id={faq.id}>
      <QuestionHeading>
        <QuestionButton
          type="button"
          data-behavior={`${faq.id}-question`}
          id={`${faq.id}-question`}
          aria-expanded={Boolean(opened[faq.id])}
          aria-controls={`${faq.id}-answer`}
          onClick={() => setOpened((current) => ({ ...current, [faq.id]: !current[faq.id] }))}
        >
          <NumberCircle aria-hidden="true">{faq.id.slice(-2)}</NumberCircle>
          <QuestionText>{faq.question}</QuestionText>
          <ToggleIcon aria-hidden="true">{opened[faq.id] ? "−" : "+"}</ToggleIcon>
        </QuestionButton>
      </QuestionHeading>
      <AnswerText data-behavior={`${faq.id}-answer`} id={`${faq.id}-answer`} hidden={!opened[faq.id]}>
        {linkedAnswer(faq.answer)}
      </AnswerText>
    </Item>
  );

  return (
    <Container data-behavior="landing-faq" id="lawlow-faq" aria-labelledby="lawlow-faq-title">
      <Header>자주 묻는 질문</Header>
      <Title id="lawlow-faq-title">로우로우, 이용 전에 궁금한 점을 확인해보세요</Title>
      <Description>질문 작성부터 이용요금, 판례 결과를 읽는 방법까지 알려드려요.</Description>
      <QuestionList>{featured.map(renderFaq)}</QuestionList>
      <MoreButton data-behavior="faq-more"
        type="button"
        aria-expanded={showMore}
        aria-controls="lawlow-faq-more"
        onClick={() => setShowMore((current) => !current)}
      >
        {showMore ? "추가 질문 접기" : `질문 ${more.length}개 더 보기`}
        <span aria-hidden="true">{showMore ? " −" : " +"}</span>
      </MoreButton>
      <QuestionList id="lawlow-faq-more" hidden={!showMore}>{more.map(renderFaq)}</QuestionList>
      <Action><StartButton data-behavior="faq-start" type="button" onClick={begin}>내 상황으로 판례 찾아보기</StartButton></Action>
    </Container>
  );
};

const Container = styled.section`
  padding: 100px 101px 110px;
  max-width: 1200px;
  margin: 0 auto;
  color: #000;
  font-family: Pretendard, sans-serif;
  [hidden] { display: none; }
  button:focus-visible, a:focus-visible {
    outline: 2px solid #087641;
    outline-offset: 4px;
  }
  @media (max-width: 1439px) { padding: 80px 64px; }
  @media (max-width: 959px) { padding: 64px 24px; }
`;
const Header = styled.p`
  font-size: 14px;
  font-weight: 600;
  text-align: center;
  margin: 0 0 24px;
`;
const Title = styled.h2`
  font-size: 32px;
  line-height: 1.5;
  text-align: center;
  word-break: keep-all;
  overflow-wrap: anywhere;
  margin: 0;
  @media (max-width: 959px) { font-size: 24px; }
`;
const Description = styled.p`
  color: #505050;
  font-size: 16px;
  line-height: 1.7;
  text-align: center;
  word-break: keep-all;
  margin: 16px 0 48px;
`;
const QuestionList = styled.div`border-top: 1px solid #ddd;`;
const Item = styled.div`
  border-bottom: 1px solid #ddd;
  scroll-margin-top: 24px;
`;
const QuestionHeading = styled.h3`margin: 0;`;
const QuestionButton = styled.button`
  display: flex;
  align-items: center;
  gap: 16px;
  width: 100%;
  padding: 24px 0;
  border: 0;
  background: transparent;
  color: inherit;
  font-family: inherit;
  text-align: left;
  cursor: pointer;
  @media (max-width: 959px) { gap: 12px; padding: 20px 0; }
`;
const NumberCircle = styled.span`
  display: grid;
  place-items: center;
  flex-shrink: 0;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: #000;
  color: #fff;
  font-size: 13px;
  font-weight: 600;
  @media (max-width: 959px) { width: 24px; height: 24px; font-size: 11px; }
`;
const QuestionText = styled.span`
  flex: 1;
  min-width: 0;
  font-size: 18px;
  line-height: 1.7;
  font-weight: 700;
  word-break: keep-all;
  overflow-wrap: anywhere;
  @media (max-width: 959px) { font-size: 16px; }
`;
const ToggleIcon = styled.span`
  flex-shrink: 0;
  width: 24px;
  font-size: 24px;
  font-weight: 400;
  text-align: center;
`;
const AnswerText = styled.p`
  margin: 0;
  padding: 0 40px 24px 48px;
  color: #505050;
  font-size: 16px;
  line-height: 1.8;
  word-break: keep-all;
  overflow-wrap: anywhere;
  a { color: #087641; text-underline-offset: 3px; }
  @media (max-width: 959px) { padding: 0 0 24px; font-size: 15px; }
`;
const MoreButton = styled.button`
  display: block;
  margin: 24px auto;
  padding: 12px 24px;
  min-height: 48px;
  border: 1px solid #ccc;
  border-radius: 24px;
  background: #fff;
  color: #303030;
  font: inherit;
  cursor: pointer;
`;
const Action = styled.div`margin-top: 40px; text-align: center;`;
const StartButton = styled.button`
  max-width: 100%;
  padding: 16px 28px;
  min-height: 56px;
  border: 0;
  border-radius: 32px;
  background: linear-gradient(104deg, #6ef4ac 22%, #78a5fe 87%);
  color: #252525;
  font-family: inherit;
  font-size: 16px;
  font-weight: 700;
  line-height: 1.5;
  cursor: pointer;
`;

export default WhyDoesItMatterSection;
